import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, AlertTriangle, Boxes, Receipt, Trash2, Printer, User, BedDouble, Store, Wallet, BarChart3, Download, IndianRupee } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function Pharmacy() {
  const [stock, setStock] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [sales, setSales] = useState([]);
  const [nearExpiry, setNearExpiry] = useState([]);
  const [medicines, setMedicines] = useState([]);

  const loadAll = async () => {
    const [s, p, sa, ne, m] = await Promise.all([
      api.get("/pharmacy/stock"),
      api.get("/pharmacy/purchases"),
      api.get("/pharmacy/sales"),
      api.get("/pharmacy/near-expiry"),
      api.get("/masters/medicines"),
    ]);
    setStock(s.data.items); setPurchases(p.data.items); setSales(sa.data.items); setNearExpiry(ne.data.items); setMedicines(m.data.items);
  };
  useEffect(() => { loadAll(); }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-end justify-between">
        <div>
          <div className="section-label">Pharmacy</div>
          <h1 className="font-heading text-3xl font-semibold">Pharmacy &amp; stock</h1>
        </div>
      </div>

      <Tabs defaultValue="sales">
        <TabsList>
          <TabsTrigger value="sales" data-testid="pharm-tab-sales"><Receipt size={14} className="mr-2" />Sales</TabsTrigger>
          <TabsTrigger value="stock" data-testid="pharm-tab-stock"><Boxes size={14} className="mr-2" />Stock</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="pharm-tab-purch">Purchases</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="pharm-tab-suppliers"><User size={14} className="mr-2" />Suppliers</TabsTrigger>
          <TabsTrigger value="expiry" data-testid="pharm-tab-expiry"><AlertTriangle size={14} className="mr-2" />Near expiry</TabsTrigger>
          <TabsTrigger value="reports" data-testid="pharm-tab-reports"><BarChart3 size={14} className="mr-2" />Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="mt-4">
          <SalesTab medicines={medicines} items={sales} reload={loadAll} />
        </TabsContent>

        <TabsContent value="stock" className="mt-4">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                <tr><th className="px-3 py-2 text-left">Medicine</th><th className="px-3 py-2 text-left">Generic</th><th className="px-3 py-2 text-left">Form</th><th className="px-3 py-2 text-right">MRP</th><th className="px-3 py-2 text-right">Stock</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {stock.map(m => (
                  <tr key={m.id}>
                    <td className="px-3 py-2 font-medium">{m.name}</td>
                    <td className="px-3 py-2">{m.generic || "—"}</td>
                    <td className="px-3 py-2">{m.form}</td>
                    <td className="px-3 py-2 text-right">₹ {m.mrp}</td>
                    <td className="px-3 py-2 text-right">{m.stock <= 20 ? <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">{m.stock}</Badge> : m.stock}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="purchases" className="mt-4">
          <PurchaseTab medicines={medicines} items={purchases} reload={loadAll} />
        </TabsContent>

        <TabsContent value="suppliers" className="mt-4">
          <SuppliersTab />
        </TabsContent>

        <TabsContent value="expiry" className="mt-4">
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                <tr><th className="px-3 py-2 text-left">Medicine</th><th className="px-3 py-2 text-left">Batch</th><th className="px-3 py-2 text-left">Expiry</th><th className="px-3 py-2 text-right">Qty</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {nearExpiry.length === 0 && <tr><td colSpan="4" className="px-3 py-10 text-center text-ink-muted">No batches expiring soon.</td></tr>}
                {nearExpiry.map(b => (
                  <tr key={b.id}><td className="px-3 py-2">{b.medicine_name}</td><td className="px-3 py-2 font-mono text-xs">{b.batch_no}</td><td className="px-3 py-2">{b.expiry}</td><td className="px-3 py-2 text-right">{b.qty_remaining}</td></tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="reports" className="mt-4">
          <PharmacyReports />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const SALE_TYPE_BADGE = {
  retail: { label: "Retail", cls: "border-slate-300 bg-slate-50 text-slate-700", icon: Store },
  opd:    { label: "OPD",    cls: "border-sky-300 bg-sky-50 text-sky-700",       icon: User },
  ipd:    { label: "IPD",    cls: "border-amber-300 bg-amber-50 text-amber-800", icon: BedDouble },
};

function SalesTab({ medicines, items, reload }) {
  const [open, setOpen] = useState(false);
  const [saleType, setSaleType] = useState("retail");
  const [rows, setRows] = useState([{ medicine_id: "", medicine_name: "", qty: 1, rate: 0, discount: 0, gst_percent: 12, hsn_code: "3004" }]);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [billDiscount, setBillDiscount] = useState(0);
  const [notes, setNotes] = useState("");
  // Retail walk-in fields
  const [custName, setCustName] = useState("");
  const [custPhone, setCustPhone] = useState("");
  const [custGstin, setCustGstin] = useState("");
  // OPD / IPD pickers
  const [patientQ, setPatientQ] = useState("");
  const [patientHits, setPatientHits] = useState([]);
  const [pickedPatient, setPickedPatient] = useState(null);
  const [admissions, setAdmissions] = useState([]);
  const [admissionId, setAdmissionId] = useState("");
  const [busy, setBusy] = useState(false);

  // Settle-payment dialog
  const [settleOpen, setSettleOpen] = useState(false);
  const [settleSale, setSettleSale] = useState(null);
  const [settleForm, setSettleForm] = useState({ amount: "", mode: "cash", reference: "", notes: "" });
  const [settleBusy, setSettleBusy] = useState(false);

  const onSettleClick = (sale) => {
    setSettleSale(sale);
    setSettleForm({ amount: Number(sale.balance || 0).toFixed(2), mode: "cash", reference: "", notes: "" });
    setSettleOpen(true);
  };

  const confirmSettle = async () => {
    if (!settleSale) return;
    const amt = parseFloat(settleForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter an amount"); return; }
    setSettleBusy(true);
    try {
      const { data } = await api.post(`/pharmacy/sales/${settleSale.id}/payment`, {
        amount: amt,
        mode: settleForm.mode,
        reference: settleForm.reference,
        notes: settleForm.notes,
      });
      toast.success(`Settled ₹${amt} · balance ₹${data.balance.toLocaleString("en-IN")}`);
      const saleId = settleSale.id;
      setSettleOpen(false);
      setSettleSale(null);
      reload();
      // Open the printable payment receipt in a new tab
      setTimeout(() => window.open(`/print/pharm-receipt/${saleId}/${data.payment_idx}`, "_blank"), 300);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSettleBusy(false); }
  };

  const reset = () => {
    setSaleType("retail");
    setRows([{ medicine_id: "", medicine_name: "", qty: 1, rate: 0, discount: 0, gst_percent: 12, hsn_code: "3004" }]);
    setPaymentMode("cash"); setBillDiscount(0); setNotes("");
    setCustName(""); setCustPhone(""); setCustGstin("");
    setPatientQ(""); setPatientHits([]); setPickedPatient(null);
    setAdmissions([]); setAdmissionId("");
  };

  const addRow = () => setRows([...rows, { medicine_id: "", medicine_name: "", qty: 1, rate: 0, discount: 0, gst_percent: 12, hsn_code: "3004" }]);
  const setRow = (i, k, v) => {
    const a = [...rows];
    a[i] = { ...a[i], [k]: ["qty", "rate", "discount", "gst_percent"].includes(k) ? Number(v) : v };
    if (k === "medicine_id") {
      const m = medicines.find(x => x.id === v);
      if (m) {
        a[i].medicine_name = m.name;
        a[i].rate = m.mrp || 0;
        a[i].gst_percent = m.gst_percent || a[i].gst_percent;
      }
    }
    setRows(a);
  };

  // Patient lookup (debounced via length>=2)
  const searchPatients = async (q) => {
    setPatientQ(q);
    if (q.length < 2) { setPatientHits([]); return; }
    const { data } = await api.get(`/patients`, { params: { q, limit: 8 } });
    setPatientHits(data.items);
  };
  const pickPatient = async (p) => {
    setPickedPatient(p);
    setPatientHits([]); setPatientQ("");
    if (saleType === "ipd") {
      const { data } = await api.get(`/ipd/admissions?status=admitted`);
      setAdmissions((data.items || []).filter(a => a.patient_id === p.id));
    }
  };

  // Totals preview
  const totals = useMemo(() => {
    let gross = 0, lineDisc = 0, tax = 0;
    for (const r of rows) {
      const g = (r.qty || 0) * (r.rate || 0);
      const d = r.discount || 0;
      const taxable = Math.max(0, g - d);
      const t = taxable * ((r.gst_percent || 0) / 100);
      gross += g; lineDisc += d; tax += t;
    }
    const sub = Math.max(0, gross - lineDisc - (parseFloat(billDiscount) || 0));
    const total = sub + tax;
    return { gross, lineDisc, sub, tax, total };
  }, [rows, billDiscount]);

  const save = async () => {
    if (rows.length === 0 || !rows.some(r => r.medicine_id)) { toast.error("Add at least one medicine"); return; }
    if (saleType !== "retail" && !pickedPatient) { toast.error("Select a patient"); return; }
    if (saleType === "ipd" && !admissionId) { toast.error("Select the IPD admission"); return; }
    setBusy(true);
    try {
      const payload = {
        sale_type: saleType,
        patient_id: pickedPatient?.id || null,
        admission_id: saleType === "ipd" ? admissionId : null,
        customer_name: saleType === "retail" ? custName : "",
        customer_phone: saleType === "retail" ? custPhone : "",
        customer_gstin: saleType === "retail" ? custGstin : "",
        items: rows.filter(r => r.medicine_id).map(r => ({
          medicine_id: r.medicine_id, medicine_name: r.medicine_name,
          batch_no: r.batch_no || "", qty: parseInt(r.qty, 10) || 0,
          rate: parseFloat(r.rate) || 0, discount: parseFloat(r.discount) || 0,
          gst_percent: parseFloat(r.gst_percent) || 0,
          hsn_code: r.hsn_code || "",
        })),
        discount_total: parseFloat(billDiscount) || 0,
        payment_mode: paymentMode,
        notes,
      };
      const { data } = await api.post("/pharmacy/sales", payload);
      toast.success(`Invoice ${data.invoice_no} · ₹${data.total} saved`);
      setOpen(false); reset(); reload();
      // open print
      setTimeout(() => window.open(`/print/pharm/${data.id}`, "_blank"), 300);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <DialogTrigger asChild>
            <Button className="bg-brand hover:bg-brand-700" data-testid="sale-new-btn">
              <Plus size={14} className="mr-2" />New sale
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">New pharmacy sale</DialogTitle>
            </DialogHeader>

            {/* Sale type chooser */}
            <div className="grid grid-cols-3 gap-2" data-testid="sale-type-picker">
              {["retail", "opd", "ipd"].map(st => {
                const cfg = SALE_TYPE_BADGE[st];
                const Icon = cfg.icon;
                const active = saleType === st;
                return (
                  <button
                    key={st}
                    type="button"
                    onClick={() => { setSaleType(st); setPickedPatient(null); setAdmissionId(""); }}
                    className={`flex items-center justify-center gap-2 rounded-lg border-2 p-3 text-sm transition ${active ? "border-brand bg-brand/5 text-brand font-semibold" : "border-border bg-white text-ink-muted hover:bg-secondary"}`}
                    data-testid={`sale-type-${st}`}
                  >
                    <Icon size={16} />{cfg.label}
                  </button>
                );
              })}
            </div>

            {/* Retail walk-in fields */}
            {saleType === "retail" && (
              <div className="rounded-lg border border-border bg-secondary/30 p-3">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Walk-in customer (optional)</div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="text-[11px]">Name</Label><Input value={custName} onChange={e => setCustName(e.target.value)} placeholder="Walk-in" data-testid="sale-cust-name" /></div>
                  <div><Label className="text-[11px]">Phone</Label><Input value={custPhone} onChange={e => setCustPhone(e.target.value)} data-testid="sale-cust-phone" /></div>
                  <div><Label className="text-[11px]">GSTIN (B2B)</Label><Input value={custGstin} onChange={e => setCustGstin(e.target.value)} placeholder="29AAAAA0000A1Z5" data-testid="sale-cust-gstin" /></div>
                </div>
              </div>
            )}

            {/* OPD / IPD patient picker */}
            {(saleType === "opd" || saleType === "ipd") && (
              <div className="rounded-lg border border-border bg-secondary/30 p-3">
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Select patient</div>
                {pickedPatient ? (
                  <div className="flex items-center justify-between rounded-md border border-border bg-white p-2 text-sm">
                    <div>
                      <span className="font-semibold">{pickedPatient.name}</span> <span className="ml-2 font-mono text-[11px] text-ink-muted">{pickedPatient.uhid}</span>
                      <span className="ml-2 text-[11px] text-ink-muted">{pickedPatient.mobile}</span>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => { setPickedPatient(null); setAdmissionId(""); }}>Change</Button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input value={patientQ} onChange={e => searchPatients(e.target.value)} placeholder="Search by name / UHID / mobile…" data-testid="sale-patient-search" />
                    {patientHits.length > 0 && (
                      <div className="absolute left-0 right-0 z-20 mt-1 max-h-56 overflow-y-auto rounded-md border border-border bg-white shadow-lg">
                        {patientHits.map(p => (
                          <button key={p.id} type="button" onClick={() => pickPatient(p)} className="block w-full border-b border-border px-3 py-2 text-left text-sm hover:bg-secondary" data-testid={`sale-patient-${p.uhid}`}>
                            <span className="font-medium">{p.name}</span> <span className="text-[11px] font-mono text-ink-muted">{p.uhid}</span> <span className="ml-2 text-[10px] text-ink-muted">{p.mobile}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {saleType === "ipd" && pickedPatient && (
                  <div className="mt-2">
                    <Label className="text-[11px]">IPD admission *</Label>
                    {admissions.length === 0 ? (
                      <div className="rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800">
                        No active admission found for this patient. Use Retail mode if this is a counter sale.
                      </div>
                    ) : (
                      <Select value={admissionId} onValueChange={setAdmissionId}>
                        <SelectTrigger data-testid="sale-admission-select"><SelectValue placeholder="Choose admission" /></SelectTrigger>
                        <SelectContent>
                          {admissions.map(a => (
                            <SelectItem key={a.id} value={a.id} data-testid={`sale-admission-${a.ipd_no}`}>
                              {a.ipd_no} · {a.ward || "—"} {a.bed_no ? `· ${a.bed_no}` : ""} · {(a.admitted_at || "").slice(0, 10)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Line items */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Medicines</div>
                <Button size="sm" variant="outline" onClick={addRow} data-testid="sale-add-row"><Plus size={12} className="mr-1" />Add line</Button>
              </div>
              <div className="space-y-2">
                {/* Header */}
                <div className="grid grid-cols-12 gap-2 text-[10px] uppercase text-ink-muted">
                  <div className="col-span-4">Medicine</div>
                  <div className="col-span-1 text-right">Qty</div>
                  <div className="col-span-2 text-right">Rate</div>
                  <div className="col-span-1 text-right">Disc</div>
                  <div className="col-span-1 text-right">GST%</div>
                  <div className="col-span-2 text-right">Line total</div>
                  <div className="col-span-1"></div>
                </div>
                {rows.map((r, i) => {
                  const g = (r.qty || 0) * (r.rate || 0);
                  const taxable = Math.max(0, g - (r.discount || 0));
                  const t = taxable * ((r.gst_percent || 0) / 100);
                  return (
                    <div key={i} className="grid grid-cols-12 gap-2">
                      <Select value={r.medicine_id} onValueChange={v => setRow(i, "medicine_id", v)}>
                        <SelectTrigger className="col-span-4" data-testid={`sale-row-med-${i}`}><SelectValue placeholder="Medicine" /></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {medicines.map(m => <SelectItem key={m.id} value={m.id}>{m.name} <span className="ml-2 text-[10px] text-ink-muted">(stock {m.stock})</span></SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input className="col-span-1 text-right" type="number" value={r.qty} onChange={e => setRow(i, "qty", e.target.value)} data-testid={`sale-row-qty-${i}`} />
                      <Input className="col-span-2 text-right" type="number" value={r.rate} onChange={e => setRow(i, "rate", e.target.value)} data-testid={`sale-row-rate-${i}`} />
                      <Input className="col-span-1 text-right" type="number" value={r.discount} onChange={e => setRow(i, "discount", e.target.value)} data-testid={`sale-row-disc-${i}`} />
                      <Input className="col-span-1 text-right" type="number" value={r.gst_percent} onChange={e => setRow(i, "gst_percent", e.target.value)} data-testid={`sale-row-gst-${i}`} />
                      <div className="col-span-2 self-center text-right text-sm font-semibold">₹ {(taxable + t).toFixed(2)}</div>
                      <Button variant="ghost" size="icon" onClick={() => setRows(rows.filter((_, idx) => idx !== i))} className="col-span-1" data-testid={`sale-row-remove-${i}`}><Trash2 size={14} className="text-rose-500" /></Button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer: bill-level discount, mode, totals */}
            <div className="grid grid-cols-2 gap-4 rounded-lg bg-secondary/30 p-3">
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[11px]">Bill discount (₹)</Label><Input type="number" value={billDiscount} onChange={e => setBillDiscount(e.target.value)} data-testid="sale-bill-disc" /></div>
                  <div>
                    <Label className="text-[11px]">Payment mode</Label>
                    <Select value={paymentMode} onValueChange={setPaymentMode}>
                      <SelectTrigger data-testid="sale-pay-mode"><SelectValue /></SelectTrigger>
                      <SelectContent>{["cash", "upi", "card", "panel", "credit"].map(m => <SelectItem key={m} value={m}>{m.replace("_"," ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-[11px]">Notes</Label>
                  <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional" data-testid="sale-notes" />
                </div>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-ink-muted">Gross</span><span>₹ {totals.gross.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-ink-muted">Line disc</span><span>− ₹ {totals.lineDisc.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-ink-muted">Bill disc</span><span>− ₹ {Number(billDiscount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-ink-muted">Taxable</span><span>₹ {totals.sub.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-ink-muted">GST (CGST+SGST)</span><span>+ ₹ {totals.tax.toFixed(2)}</span></div>
                <div className="flex justify-between border-t border-border pt-1 font-heading text-base"><span>Grand total</span><span className="text-brand font-bold">₹ {totals.total.toFixed(2)}</span></div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-700" data-testid="sale-save-btn">
                <Receipt size={14} className="mr-2" />Save &amp; print invoice
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-3 py-2 text-left">Invoice</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Type</th>
              <th className="px-3 py-2 text-left">Customer / Patient</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Paid</th>
              <th className="px-3 py-2 text-right">Balance</th>
              <th className="px-3 py-2 text-left">Mode</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={10} className="px-3 py-10 text-center text-ink-muted">No sales yet.</td></tr>}
            {items.map(s => {
              const cfg = SALE_TYPE_BADGE[s.sale_type] || SALE_TYPE_BADGE.retail;
              const balance = Number(s.balance ?? 0);
              const isCredit = s.payment_mode === "credit";
              const status = s.status || (isCredit ? "due" : "paid");
              const statusCls = status === "paid"
                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                : status === "partial"
                  ? "border-amber-300 bg-amber-50 text-amber-700"
                  : "border-rose-300 bg-rose-50 text-rose-700";
              return (
                <tr key={s.id} data-testid={`sale-row-${s.invoice_no || s.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">
                    {s.invoice_no
                      ? <a href={`/print/pharm/${s.id}`} target="_blank" rel="noreferrer" className="text-brand hover:underline">{s.invoice_no}</a>
                      : <span className="text-ink-muted">{s.id.slice(0, 8)}</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">{(s.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={cfg.cls}>{cfg.label}</Badge></td>
                  <td className="px-3 py-2 text-xs">
                    <div className="font-medium">{s.patient_name || "Walk-in"}</div>
                    {s.patient_uhid && <div className="text-[10px] font-mono text-ink-muted">{s.patient_uhid}</div>}
                    {s.ipd_no && <div className="text-[10px] font-mono text-amber-700">{s.ipd_no}</div>}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">₹ {Number(s.total).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 text-right text-emerald-700">₹ {Number(s.paid_amount ?? (isCredit ? 0 : s.total)).toLocaleString("en-IN")}</td>
                  <td className={`px-3 py-2 text-right ${balance > 0 ? "text-rose-700 font-semibold" : "text-ink-muted"}`}>{balance > 0 ? `₹ ${balance.toLocaleString("en-IN")}` : "—"}</td>
                  <td className="px-3 py-2 capitalize text-xs">{s.payment_mode}</td>
                  <td className="px-3 py-2 text-xs"><Badge variant="outline" className={statusCls}>{status}</Badge></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {s.invoice_no && <a href={`/print/pharm/${s.id}`} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" data-testid={`sale-print-${s.invoice_no}`}><Printer size={11} /></Button></a>}
                    {balance > 0 && (
                      <Button size="sm" variant="outline" className="ml-1 border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => onSettleClick(s)} data-testid={`sale-settle-${s.invoice_no}`}>
                        <Wallet size={11} className="mr-1" />Settle
                      </Button>
                    )}
                    {(s.payments || []).length > 0 && (
                      <SettledReceiptsMenu sale={s} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Settle-payment dialog */}
      <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Settle payment — {settleSale?.invoice_no}</DialogTitle>
          </DialogHeader>
          {settleSale && (
            <>
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                Outstanding on this credit invoice: <b>₹ {Number(settleSale.balance || 0).toLocaleString("en-IN")}</b>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <Label>Amount *</Label>
                  <Input type="number" value={settleForm.amount} onChange={e => setSettleForm({ ...settleForm, amount: e.target.value })} data-testid="settle-amount" />
                </div>
                <div>
                  <Label>Mode</Label>
                  <Select value={settleForm.mode} onValueChange={v => setSettleForm({ ...settleForm, mode: v })}>
                    <SelectTrigger data-testid="settle-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>{["cash", "upi", "card", "bank_transfer", "cheque"].map(m => <SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Reference (UPI / cheque no.)</Label>
                  <Input value={settleForm.reference} onChange={e => setSettleForm({ ...settleForm, reference: e.target.value })} data-testid="settle-ref" />
                </div>
                <div className="col-span-2">
                  <Label>Notes</Label>
                  <Input value={settleForm.notes} onChange={e => setSettleForm({ ...settleForm, notes: e.target.value })} />
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <Button onClick={confirmSettle} disabled={settleBusy || !parseFloat(settleForm.amount)} className="bg-brand hover:bg-brand-700" data-testid="settle-confirm">
                  Receive ₹{Number(settleForm.amount || 0).toLocaleString("en-IN")} &amp; print receipt
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SettledReceiptsMenu({ sale }) {
  // Shows a tiny dropdown with all past payments → click to open the receipt print.
  const [open, setOpen] = useState(false);
  const payments = sale.payments || [];
  if (payments.length === 0) return null;
  return (
    <span className="relative ml-1 inline-block">
      <Button size="sm" variant="ghost" onClick={() => setOpen(v => !v)} className="text-emerald-700" data-testid={`receipt-menu-${sale.invoice_no}`}>
        <Receipt size={11} />
      </Button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-56 rounded-md border border-border bg-white p-1 text-xs shadow-lg">
          <div className="border-b border-border px-2 py-1 text-[10px] uppercase tracking-wider text-ink-muted">Payment receipts</div>
          {payments.map((p, i) => (
            <a key={i} href={`/print/pharm-receipt/${sale.id}/${i}`} target="_blank" rel="noreferrer" className="flex items-center justify-between px-2 py-1.5 hover:bg-secondary" onClick={() => setOpen(false)}>
              <span className="font-mono">#{i + 1}</span>
              <span className="text-emerald-700 font-semibold">₹{Number(p.amount).toLocaleString("en-IN")}</span>
              <span className="text-[10px] text-ink-muted">{(p.received_at || "").slice(0, 10)}</span>
            </a>
          ))}
        </div>
      )}
    </span>
  );
}

function PurchaseTab({ medicines, items, reload }) {
  const [open, setOpen] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [docType, setDocType] = useState("invoice");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMode, setPaymentMode] = useState("credit");
  const [billDiscount, setBillDiscount] = useState(0);
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/pharmacy/suppliers?active_only=true").then(r => setSuppliers(r.data.items || []));
  }, []);

  const addRow = () => setRows([...rows, {
    medicine_id: "", medicine_name: "",
    batch_no: "", lot_no: "", expiry: "",
    qty: 1, qty_free: 0,
    purchase_rate: 0, mrp: 0, sale_rate: 0,
    discount_pct: 0, gst_percent: 12,
    free_items: [],
  }]);
  const setRow = (i, k, v) => {
    const a = [...rows];
    a[i] = { ...a[i], [k]: ["qty","qty_free","purchase_rate","mrp","sale_rate","discount_pct","gst_percent"].includes(k) ? Number(v) : v };
    if (k === "medicine_id") {
      const m = medicines.find(x => x.id === v);
      if (m) { a[i].medicine_name = m.name; a[i].mrp = m.mrp || 0; a[i].sale_rate = m.mrp || 0; a[i].gst_percent = m.gst_percent || a[i].gst_percent; }
    }
    setRows(a);
  };
  const addFreeItem = (rowIdx) => {
    const a = [...rows];
    const fi = (a[rowIdx].free_items || []).slice();
    fi.push({ medicine_id: "", medicine_name: "", batch_no: "", expiry: "", qty: 1, mrp: 0 });
    a[rowIdx] = { ...a[rowIdx], free_items: fi };
    setRows(a);
  };
  const setFreeItem = (rowIdx, fIdx, k, v) => {
    const a = [...rows];
    const fi = (a[rowIdx].free_items || []).slice();
    fi[fIdx] = { ...fi[fIdx], [k]: ["qty","mrp"].includes(k) ? Number(v) : v };
    if (k === "medicine_id") {
      const m = medicines.find(x => x.id === v);
      if (m) { fi[fIdx].medicine_name = m.name; fi[fIdx].mrp = m.mrp || 0; }
    }
    a[rowIdx] = { ...a[rowIdx], free_items: fi };
    setRows(a);
  };
  const removeFreeItem = (rowIdx, fIdx) => {
    const a = [...rows];
    const fi = (a[rowIdx].free_items || []).filter((_, idx) => idx !== fIdx);
    a[rowIdx] = { ...a[rowIdx], free_items: fi };
    setRows(a);
  };

  const totals = useMemo(() => {
    let gross = 0, lineDisc = 0, tax = 0;
    for (const r of rows) {
      const g = (r.qty || 0) * (r.purchase_rate || 0);
      const d = g * ((r.discount_pct || 0) / 100);
      const taxable = Math.max(0, g - d);
      const t = taxable * ((r.gst_percent || 0) / 100);
      gross += g; lineDisc += d; tax += t;
    }
    const sub = Math.max(0, gross - lineDisc - (parseFloat(billDiscount) || 0));
    return { gross, lineDisc, sub, tax, total: sub + tax };
  }, [rows, billDiscount]);

  const reset = () => {
    setSupplierId(""); setDocType("invoice"); setInvoiceNo("");
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    setPaymentMode("credit"); setBillDiscount(0); setNotes(""); setRows([]);
  };

  const save = async () => {
    if (!supplierId) { toast.error("Pick a supplier"); return; }
    if (rows.length === 0 || !rows.some(r => r.medicine_id)) { toast.error("Add at least one item"); return; }
    if (docType === "invoice" && !invoiceNo.trim()) { toast.error("Invoice no. is required when receiving against an invoice"); return; }
    const sup = suppliers.find(s => s.id === supplierId);
    setBusy(true);
    try {
      const { data } = await api.post("/pharmacy/purchases", {
        supplier_id: supplierId,
        supplier: sup?.name || "",
        doc_type: docType,
        invoice_no: invoiceNo,
        invoice_date: invoiceDate,
        payment_mode: paymentMode,
        items: rows.filter(r => r.medicine_id).map(r => ({
          medicine_id: r.medicine_id, medicine_name: r.medicine_name,
          batch_no: r.batch_no, lot_no: r.lot_no, expiry: r.expiry,
          qty: parseInt(r.qty, 10) || 0,
          qty_free: parseInt(r.qty_free, 10) || 0,
          purchase_rate: parseFloat(r.purchase_rate) || 0,
          mrp: parseFloat(r.mrp) || 0,
          sale_rate: parseFloat(r.sale_rate) || parseFloat(r.mrp) || 0,
          discount_pct: parseFloat(r.discount_pct) || 0,
          gst_percent: parseFloat(r.gst_percent) || 0,
          free_items: (r.free_items || []).filter(f => f.medicine_id && Number(f.qty) > 0).map(f => ({
            medicine_id: f.medicine_id, medicine_name: f.medicine_name,
            batch_no: f.batch_no || r.batch_no,
            expiry: f.expiry || r.expiry,
            qty: parseInt(f.qty, 10) || 0,
            mrp: parseFloat(f.mrp) || 0,
          })),
        })),
        discount_total: parseFloat(billDiscount) || 0,
        notes,
      });
      toast.success(`Purchase recorded · ₹${Number(data.total).toLocaleString("en-IN")} · stock updated`);
      setOpen(false); reset(); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
          <DialogTrigger asChild>
            <Button className="bg-brand hover:bg-brand-700" data-testid="purch-new-btn"><Plus size={14} className="mr-2" />New purchase</Button>
          </DialogTrigger>
          <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">New purchase — Goods receipt</DialogTitle></DialogHeader>

            {/* Doc type toggle */}
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "invoice", label: "Against Invoice (GST credit)" },
                { v: "challan", label: "Against Challan (stock-in only)" },
              ].map(o => (
                <button key={o.v} type="button" onClick={() => setDocType(o.v)}
                  className={`rounded-lg border-2 p-2.5 text-sm transition ${docType === o.v ? "border-brand bg-brand/5 text-brand font-semibold" : "border-border bg-white text-ink-muted hover:bg-secondary"}`}
                  data-testid={`purch-doc-${o.v}`}>
                  {o.label}
                </button>
              ))}
            </div>

            {/* Header fields */}
            <div className="grid grid-cols-3 gap-3 rounded-lg border border-border bg-secondary/30 p-3">
              <div className="col-span-2">
                <Label>Supplier *</Label>
                <Select value={supplierId} onValueChange={setSupplierId}>
                  <SelectTrigger data-testid="purch-supplier"><SelectValue placeholder={suppliers.length ? "Choose supplier" : "Add a supplier first (Suppliers tab)"} /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} {s.gstin && <span className="ml-2 text-[10px] font-mono text-ink-muted">{s.gstin}</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{docType === "invoice" ? "Invoice no." : "Challan no."} {docType === "invoice" ? "*" : ""}</Label>
                <Input value={invoiceNo} onChange={e => setInvoiceNo(e.target.value)} data-testid="purch-invoice-no" />
              </div>
              <div>
                <Label>{docType === "invoice" ? "Invoice" : "Challan"} date</Label>
                <Input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} data-testid="purch-invoice-date" />
              </div>
              <div>
                <Label>Payment mode</Label>
                <Select value={paymentMode} onValueChange={setPaymentMode}>
                  <SelectTrigger data-testid="purch-pay-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>{["cash","upi","card","bank_transfer","credit"].map(m => <SelectItem key={m} value={m}>{m.replace("_"," ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bill discount (₹)</Label>
                <Input type="number" value={billDiscount} onChange={e => setBillDiscount(e.target.value)} data-testid="purch-bill-disc" />
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="mb-1 flex items-center justify-between">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Items</div>
                <Button size="sm" variant="outline" onClick={addRow} data-testid="purch-add-row"><Plus size={12} className="mr-1" />Add item</Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-secondary text-[10px] uppercase tracking-wider text-ink-muted">
                    <tr>
                      <th className="px-1.5 py-1 text-left">Medicine</th>
                      <th className="px-1.5 py-1 text-left">Batch</th>
                      <th className="px-1.5 py-1 text-left">Expiry</th>
                      <th className="px-1.5 py-1 text-right">Qty</th>
                      <th className="px-1.5 py-1 text-right" title="Free / bonus units — added to stock, not billed">Free</th>
                      <th className="px-1.5 py-1 text-right">Cost</th>
                      <th className="px-1.5 py-1 text-right">MRP</th>
                      <th className="px-1.5 py-1 text-right">Sale rate</th>
                      <th className="px-1.5 py-1 text-right">Disc %</th>
                      <th className="px-1.5 py-1 text-right">GST %</th>
                      <th className="px-1.5 py-1 text-right">Line ₹</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && <tr><td colSpan={12} className="px-2 py-4 text-center text-ink-muted">No items yet. Click <b>Add item</b> to begin.</td></tr>}
                    {rows.map((r, i) => {
                      const g = (r.qty || 0) * (r.purchase_rate || 0);
                      const d = g * ((r.discount_pct || 0) / 100);
                      const tax = Math.max(0, g - d) * ((r.gst_percent || 0) / 100);
                      return (
                        <React.Fragment key={i}>
                        <tr className="border-t border-border">
                          <td className="px-1 py-1">
                            <Select value={r.medicine_id} onValueChange={v => setRow(i, "medicine_id", v)}>
                              <SelectTrigger className="h-7 w-44" data-testid={`purch-med-${i}`}><SelectValue placeholder="Medicine" /></SelectTrigger>
                              <SelectContent className="max-h-72">{medicines.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                          <td className="px-1 py-1"><Input className="h-7 w-20" value={r.batch_no} onChange={e => setRow(i, "batch_no", e.target.value)} data-testid={`purch-batch-${i}`} /></td>
                          <td className="px-1 py-1"><Input className="h-7 w-28" type="date" value={r.expiry} onChange={e => setRow(i, "expiry", e.target.value)} data-testid={`purch-exp-${i}`} /></td>
                          <td className="px-1 py-1"><Input className="h-7 w-14 text-right" type="number" value={r.qty} onChange={e => setRow(i, "qty", e.target.value)} data-testid={`purch-qty-${i}`} /></td>
                          <td className="px-1 py-1"><Input className="h-7 w-12 text-right" type="number" value={r.qty_free} onChange={e => setRow(i, "qty_free", e.target.value)} data-testid={`purch-free-${i}`} /></td>
                          <td className="px-1 py-1"><Input className="h-7 w-16 text-right" type="number" value={r.purchase_rate} onChange={e => setRow(i, "purchase_rate", e.target.value)} data-testid={`purch-rate-${i}`} /></td>
                          <td className="px-1 py-1"><Input className="h-7 w-16 text-right" type="number" value={r.mrp} onChange={e => setRow(i, "mrp", e.target.value)} /></td>
                          <td className="px-1 py-1"><Input className="h-7 w-16 text-right" type="number" value={r.sale_rate} onChange={e => setRow(i, "sale_rate", e.target.value)} data-testid={`purch-sale-${i}`} /></td>
                          <td className="px-1 py-1"><Input className="h-7 w-12 text-right" type="number" value={r.discount_pct} onChange={e => setRow(i, "discount_pct", e.target.value)} data-testid={`purch-disc-${i}`} /></td>
                          <td className="px-1 py-1"><Input className="h-7 w-12 text-right" type="number" value={r.gst_percent} onChange={e => setRow(i, "gst_percent", e.target.value)} /></td>
                          <td className="px-1 py-1 text-right font-semibold">{(Math.max(0, g - d) + tax).toFixed(2)}</td>
                          <td className="px-1 py-1"><Button variant="ghost" size="icon" onClick={() => setRows(rows.filter((_, idx) => idx !== i))}><Trash2 size={14} className="text-rose-500" /></Button></td>
                        </tr>
                        {/* Free-scheme items (different medicine given free with this paid line) */}
                        {(r.free_items || []).map((fi, fIdx) => (
                          <tr key={`fi-${i}-${fIdx}`} className="bg-emerald-50/60">
                            <td className="px-1 py-1">
                              <div className="flex items-center gap-1">
                                <Badge variant="outline" className="border-emerald-300 bg-white text-[9px] text-emerald-700">FREE</Badge>
                                <Select value={fi.medicine_id} onValueChange={v => setFreeItem(i, fIdx, "medicine_id", v)}>
                                  <SelectTrigger className="h-7 w-36" data-testid={`purch-free-med-${i}-${fIdx}`}><SelectValue placeholder="Free item" /></SelectTrigger>
                                  <SelectContent className="max-h-72">{medicines.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                            </td>
                            <td className="px-1 py-1"><Input className="h-7 w-20" value={fi.batch_no} onChange={e => setFreeItem(i, fIdx, "batch_no", e.target.value)} placeholder="same" data-testid={`purch-free-batch-${i}-${fIdx}`} /></td>
                            <td className="px-1 py-1"><Input className="h-7 w-28" type="date" value={fi.expiry} onChange={e => setFreeItem(i, fIdx, "expiry", e.target.value)} data-testid={`purch-free-exp-${i}-${fIdx}`} /></td>
                            <td className="px-1 py-1"><Input className="h-7 w-14 text-right" type="number" value={fi.qty} onChange={e => setFreeItem(i, fIdx, "qty", e.target.value)} data-testid={`purch-free-qty-${i}-${fIdx}`} /></td>
                            <td colSpan={2} className="px-1 py-1 text-[10px] italic text-emerald-700">Free with {r.medicine_name || "above item"}</td>
                            <td className="px-1 py-1"><Input className="h-7 w-16 text-right" type="number" value={fi.mrp} onChange={e => setFreeItem(i, fIdx, "mrp", e.target.value)} placeholder="MRP" /></td>
                            <td colSpan={3} className="px-1 py-1 text-right text-[10px] text-emerald-700 italic">₹ 0 (scheme bonus)</td>
                            <td className="px-1 py-1 text-right text-[10px] text-emerald-700">—</td>
                            <td className="px-1 py-1"><Button variant="ghost" size="icon" onClick={() => removeFreeItem(i, fIdx)} data-testid={`purch-free-del-${i}-${fIdx}`}><Trash2 size={12} className="text-rose-400" /></Button></td>
                          </tr>
                        ))}
                        {r.medicine_id && (
                          <tr>
                            <td colSpan={12} className="px-2 pb-2">
                              <Button size="sm" variant="ghost" className="h-6 text-[10px] text-emerald-700 hover:bg-emerald-50" onClick={() => addFreeItem(i)} data-testid={`purch-add-free-${i}`}>
                                + Add free item (different medicine — e.g. Paracetamol → 2 Hand-wash free)
                              </Button>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer */}
            <div className="grid grid-cols-2 gap-4 rounded-lg bg-secondary/30 p-3">
              <div>
                <Label>Notes</Label>
                <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional remarks (transport, broken pcs, etc.)" />
                <div className="mt-2 text-[10px] text-ink-muted">
                  Free units are added to <b>stock</b> but not to billed total. Cost &amp; MRP update the batch; Sale rate becomes the default POS price for new sales.
                </div>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-ink-muted">Gross</span><span>₹ {totals.gross.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-ink-muted">Line discounts</span><span>− ₹ {totals.lineDisc.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-ink-muted">Bill discount</span><span>− ₹ {Number(billDiscount || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-ink-muted">Taxable</span><span>₹ {totals.sub.toFixed(2)}</span></div>
                <div className="flex justify-between"><span className="text-ink-muted">GST</span><span>+ ₹ {totals.tax.toFixed(2)}</span></div>
                <div className="flex justify-between border-t border-border pt-1 font-heading text-base"><span>Grand total</span><span className="text-brand font-bold">₹ {totals.total.toFixed(2)}</span></div>
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-700" data-testid="purch-save-btn">Record purchase</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Supplier</th>
              <th className="px-3 py-2 text-left">Doc</th>
              <th className="px-3 py-2 text-left">Ref no.</th>
              <th className="px-3 py-2 text-right">Items</th>
              <th className="px-3 py-2 text-right">GST</th>
              <th className="px-3 py-2 text-right">Total</th>
              <th className="px-3 py-2 text-right">Paid</th>
              <th className="px-3 py-2 text-right">Balance</th>
              <th className="px-3 py-2 text-left">Mode / Status</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={11} className="px-3 py-10 text-center text-ink-muted">No purchases.</td></tr>}
            {items.map(p => {
              const balance = Number(p.balance || 0);
              const paid = Number(p.paid_amount || 0);
              const status = p.status || (p.payment_mode === "credit" ? "due" : "paid");
              return (
              <tr key={p.id}>
                <td className="px-3 py-2 text-xs">{(p.created_at || "").slice(0, 10)}</td>
                <td className="px-3 py-2"><div className="font-medium">{p.supplier}</div>{p.supplier_gstin && <div className="text-[10px] font-mono text-ink-muted">{p.supplier_gstin}</div>}</td>
                <td className="px-3 py-2 text-xs">
                  <Badge variant="outline" className={p.doc_type === "challan" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"}>
                    {p.doc_type === "challan" ? "Challan" : "Invoice"}
                  </Badge>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{p.invoice_no || "—"}{p.invoice_date && <div className="text-[10px] text-ink-muted">{p.invoice_date}</div>}</td>
                <td className="px-3 py-2 text-right">{(p.items || []).length}</td>
                <td className="px-3 py-2 text-right text-xs">{p.tax_total !== undefined ? `₹ ${Number(p.tax_total).toFixed(2)}` : "—"}</td>
                <td className="px-3 py-2 text-right font-semibold">₹ {Number(p.total).toLocaleString("en-IN")}</td>
                <td className="px-3 py-2 text-right text-xs text-emerald-700">₹ {paid.toFixed(2)}</td>
                <td className="px-3 py-2 text-right text-xs font-semibold" style={{ color: balance > 0 ? "#b91c1c" : "#0f766e" }}>₹ {balance.toFixed(2)}</td>
                <td className="px-3 py-2 capitalize text-xs">
                  <div>{(p.payment_mode || "—").replace("_", " ")}</div>
                  <Badge variant="outline" className={
                    status === "paid" ? "border-emerald-300 bg-emerald-50 text-emerald-700" :
                    status === "partial" ? "border-amber-300 bg-amber-50 text-amber-700" :
                    "border-rose-300 bg-rose-50 text-rose-700"
                  }>{status}</Badge>
                </td>
                <td className="px-3 py-2 text-right">
                  {balance > 0 && <SettlePurchaseButton purchase={p} reload={reload} />}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

const EMPTY_SUPPLIER = { name: "", contact_person: "", phone: "", email: "", address: "", gstin: "", drug_license_no: "", payment_terms: "Net 30", opening_balance: 0, notes: "", active: true };

function SuppliersTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_SUPPLIER);
  const [busy, setBusy] = useState(false);

  const load = async () => { const { data } = await api.get("/pharmacy/suppliers"); setItems(data.items || []); };
  useEffect(() => { load(); }, []);

  const startAdd = () => { setEditing(null); setForm(EMPTY_SUPPLIER); setOpen(true); };
  const startEdit = (s) => { setEditing(s.id); setForm({ ...EMPTY_SUPPLIER, ...s }); setOpen(true); };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Supplier name is required"); return; }
    setBusy(true);
    try {
      if (editing) await api.patch(`/pharmacy/suppliers/${editing}`, form);
      else await api.post(`/pharmacy/suppliers`, form);
      toast.success(editing ? "Updated" : "Added");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"?\nIf any purchase references it, it will be soft-deactivated.`)) return;
    try {
      const { data } = await api.delete(`/pharmacy/suppliers/${s.id}`);
      toast.success(data.soft_deleted ? "Deactivated (has purchase history)" : "Deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={startAdd} className="bg-brand hover:bg-brand-700" data-testid="sup-add-btn"><Plus size={14} className="mr-2" />Add supplier</Button>
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-3 py-2 text-left">Name</th>
              <th className="px-3 py-2 text-left">GSTIN</th>
              <th className="px-3 py-2 text-left">Drug Lic.</th>
              <th className="px-3 py-2 text-left">Contact</th>
              <th className="px-3 py-2 text-left">Terms</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.length === 0 && <tr><td colSpan={7} className="px-3 py-10 text-center text-ink-muted">No suppliers yet. Click <b>Add supplier</b>.</td></tr>}
            {items.map(s => (
              <tr key={s.id} data-testid={`sup-row-${s.id}`}>
                <td className="px-3 py-2"><div className="font-medium">{s.name}</div>{s.address && <div className="text-[10px] text-ink-muted">{s.address}</div>}</td>
                <td className="px-3 py-2 font-mono text-xs">{s.gstin || "—"}</td>
                <td className="px-3 py-2 font-mono text-xs">{s.drug_license_no || "—"}</td>
                <td className="px-3 py-2 text-xs">{[s.contact_person, s.phone, s.email].filter(Boolean).join(" · ") || "—"}</td>
                <td className="px-3 py-2 text-xs">{s.payment_terms || "—"}</td>
                <td className="px-3 py-2">{s.active === false ? <Badge variant="outline" className="border-rose-300 bg-rose-50 text-rose-700">Inactive</Badge> : <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">Active</Badge>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => startEdit(s)} data-testid={`sup-edit-${s.id}`}><Wallet size={11} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(s)} className="text-rose-700 hover:bg-rose-50" data-testid={`sup-del-${s.id}`}><Trash2 size={11} /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit supplier" : "Add supplier"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2"><Label>Supplier name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="sup-form-name" /></div>
            <div><Label>GSTIN</Label><Input value={form.gstin} onChange={e => setForm({ ...form, gstin: e.target.value })} placeholder="29AAAAA0000A1Z5" data-testid="sup-form-gstin" /></div>
            <div><Label>Drug license no.</Label><Input value={form.drug_license_no} onChange={e => setForm({ ...form, drug_license_no: e.target.value })} data-testid="sup-form-dl" /></div>
            <div><Label>Contact person</Label><Input value={form.contact_person} onChange={e => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Payment terms</Label><Input value={form.payment_terms} onChange={e => setForm({ ...form, payment_terms: e.target.value })} placeholder="Net 30 / COD" /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Opening balance (₹)</Label><Input type="number" value={form.opening_balance} onChange={e => setForm({ ...form, opening_balance: Number(e.target.value) })} /></div>
            <div className="flex items-center gap-2 sm:col-span-2">
              <input type="checkbox" id="sup-active" checked={form.active !== false} onChange={e => setForm({ ...form, active: e.target.checked })} />
              <label htmlFor="sup-active" className="text-sm">Active</label>
            </div>
            <div className="sm:col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <div className="mt-3 flex justify-end"><Button onClick={save} disabled={busy} className="bg-brand hover:bg-brand-700" data-testid="sup-form-save">{editing ? "Update" : "Add"}</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}



// ============================================================
// Settle Purchase (credit purchase → record supplier payment)
// ============================================================
function SettlePurchaseButton({ purchase, reload }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(Number(purchase.balance || 0).toFixed(2));
  const [mode, setMode] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setAmount(Number(purchase.balance || 0).toFixed(2)); setMode("bank_transfer"); setReference(""); setNotes(""); } }, [open, purchase.balance]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post(`/pharmacy/purchases/${purchase.id}/payment`, { amount: amt, mode, reference, notes });
      toast.success(`Payment of ₹${amt} recorded`);
      setOpen(false); reload();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" data-testid={`purch-settle-${purchase.id}`}>
          <IndianRupee size={11} className="mr-1" />Settle
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="font-heading">Settle supplier payment</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="rounded-md border border-border bg-secondary/40 p-2 text-xs">
            <div className="flex justify-between"><span>Supplier</span><span className="font-medium">{purchase.supplier}</span></div>
            <div className="flex justify-between"><span>Invoice</span><span className="font-mono">{purchase.invoice_no || "—"}</span></div>
            <div className="flex justify-between"><span>Bill total</span><span>₹ {Number(purchase.total || 0).toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Already paid</span><span className="text-emerald-700">₹ {Number(purchase.paid_amount || 0).toFixed(2)}</span></div>
            <div className="flex justify-between text-rose-700"><span>Outstanding</span><span className="font-bold">₹ {Number(purchase.balance || 0).toFixed(2)}</span></div>
          </div>
          <div><Label>Amount paying *</Label><Input type="number" value={amount} onChange={e => setAmount(e.target.value)} data-testid="purch-settle-amt" /></div>
          <div><Label>Mode</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="purch-settle-mode"><SelectValue /></SelectTrigger>
              <SelectContent>{["cash","upi","card","bank_transfer","cheque"].map(m => <SelectItem key={m} value={m}>{m.replace("_"," ")}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Reference (UTR / cheque no.)</Label><Input value={reference} onChange={e => setReference(e.target.value)} data-testid="purch-settle-ref" /></div>
          <div><Label>Notes</Label><Input value={notes} onChange={e => setNotes(e.target.value)} /></div>
          <div className="flex justify-end"><Button onClick={submit} disabled={busy} className="bg-brand hover:bg-brand-700" data-testid="purch-settle-save">Record payment</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================
// Pharmacy Reports — sales · purchases · stock · suppliers · near-expiry · GST
// ============================================================
const today = () => new Date().toISOString().slice(0, 10);
const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10); };
const toCsv = (rows, headers) => {
  const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  return [headers.map(h => esc(h.label)).join(","), ...rows.map(r => headers.map(h => esc(typeof h.get === "function" ? h.get(r) : r[h.key])).join(","))].join("\n");
};
const downloadCsv = (filename, content) => {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};
const openPdf = (type, params) => {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v !== undefined && v !== null && v !== "") qs.set(k, v);
  }
  window.open(`/print/pharmacy-report/${type}?${qs}`, "_blank");
};

function PharmacyReports() {
  return (
    <Tabs defaultValue="sales">
      <TabsList className="flex-wrap">
        <TabsTrigger value="sales" data-testid="rep-tab-sales">Sales</TabsTrigger>
        <TabsTrigger value="purchases" data-testid="rep-tab-purchases">Purchases</TabsTrigger>
        <TabsTrigger value="stock" data-testid="rep-tab-stock">Stock</TabsTrigger>
        <TabsTrigger value="suppliers" data-testid="rep-tab-suppliers">Suppliers</TabsTrigger>
        <TabsTrigger value="expiry" data-testid="rep-tab-expiry">Near expiry</TabsTrigger>
        <TabsTrigger value="gst" data-testid="rep-tab-gst">GST</TabsTrigger>
      </TabsList>

      <TabsContent value="sales" className="mt-3"><SalesReport /></TabsContent>
      <TabsContent value="purchases" className="mt-3"><PurchasesReport /></TabsContent>
      <TabsContent value="stock" className="mt-3"><StockReport /></TabsContent>
      <TabsContent value="suppliers" className="mt-3"><SuppliersReport /></TabsContent>
      <TabsContent value="expiry" className="mt-3"><NearExpiryReport /></TabsContent>
      <TabsContent value="gst" className="mt-3"><GstReport /></TabsContent>
    </Tabs>
  );
}

function DateRangeBar({ from, to, setFrom, setTo, onExport, onPrint, extra }) {
  return (
    <Card className="mb-3 p-3">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-ink-muted">From</Label>
          <Input type="date" value={from} onChange={e => setFrom(e.target.value)} className="h-8 w-40" data-testid="rep-from" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-ink-muted">To</Label>
          <Input type="date" value={to} onChange={e => setTo(e.target.value)} className="h-8 w-40" data-testid="rep-to" />
        </div>
        {extra}
        <div className="flex-1" />
        {onPrint && <Button size="sm" variant="outline" onClick={onPrint} data-testid="rep-pdf"><Printer size={12} className="mr-1" />Print PDF</Button>}
        {onExport && <Button size="sm" variant="outline" onClick={onExport} data-testid="rep-csv"><Download size={12} className="mr-1" />Export CSV</Button>}
      </div>
    </Card>
  );
}

function StatCard({ label, value, tone = "default" }) {
  const tones = {
    default: "border-border bg-white",
    pos: "border-emerald-200 bg-emerald-50",
    neg: "border-rose-200 bg-rose-50",
    warn: "border-amber-200 bg-amber-50",
  };
  return (
    <Card className={`p-3 ${tones[tone]}`}>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-muted">{label}</div>
      <div className="font-heading text-lg font-semibold">{value}</div>
    </Card>
  );
}

function SalesReport() {
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [paymentMode, setPaymentMode] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState({ items: [], summary: {} });

  const load = async () => {
    const params = new URLSearchParams({ from, to });
    if (paymentMode) params.set("payment_mode", paymentMode);
    if (status) params.set("status", status);
    const { data } = await api.get(`/pharmacy/reports/sales?${params}`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, paymentMode, status]);

  const exportCsv = () => {
    const headers = [
      { label: "Invoice", key: "invoice_no" },
      { label: "Date", get: (r) => (r.created_at || "").slice(0, 10) },
      { label: "Type", key: "sale_type" },
      { label: "Customer/Patient", key: "patient_name" },
      { label: "UHID", key: "patient_uhid" },
      { label: "Mode", key: "payment_mode" },
      { label: "Status", key: "status" },
      { label: "Total", key: "total" },
      { label: "Paid", key: "paid_amount" },
      { label: "Balance", key: "balance" },
      { label: "CGST", key: "cgst_total" },
      { label: "SGST", key: "sgst_total" },
    ];
    downloadCsv(`pharmacy-sales-${from}_${to}.csv`, toCsv(data.items || [], headers));
  };

  return (
    <div>
      <DateRangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} onExport={exportCsv}
        onPrint={() => openPdf("sales", { from, to, payment_mode: paymentMode, status })}
        extra={<>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Mode</Label>
            <Select value={paymentMode || "all"} onValueChange={v => setPaymentMode(v === "all" ? "" : v)}>
              <SelectTrigger className="h-8 w-32" data-testid="rep-sales-mode"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>{["all","cash","upi","card","panel","credit"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Status</Label>
            <Select value={status || "all"} onValueChange={v => setStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="h-8 w-32" data-testid="rep-sales-status"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>{["all","paid","partial","due"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </>}
      />
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Invoices" value={data.summary?.count ?? 0} />
        <StatCard label="Total ₹" value={`₹ ${Number(data.summary?.total || 0).toLocaleString("en-IN")}`} tone="pos" />
        <StatCard label="Paid ₹" value={`₹ ${Number(data.summary?.paid || 0).toLocaleString("en-IN")}`} />
        <StatCard label="Outstanding ₹" value={`₹ ${Number(data.summary?.outstanding || 0).toLocaleString("en-IN")}`} tone={Number(data.summary?.outstanding || 0) > 0 ? "neg" : "default"} />
        <StatCard label="GST ₹" value={`₹ ${Number(data.summary?.gst || 0).toLocaleString("en-IN")}`} />
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-wider text-ink-muted">
            <tr><th className="px-2 py-2 text-left">Invoice</th><th className="px-2 py-2 text-left">Date</th><th className="px-2 py-2 text-left">Type</th><th className="px-2 py-2 text-left">Customer / Patient</th><th className="px-2 py-2 text-left">Mode</th><th className="px-2 py-2 text-right">Total</th><th className="px-2 py-2 text-right">Paid</th><th className="px-2 py-2 text-right">Balance</th><th className="px-2 py-2 text-left">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(data.items || []).length === 0 && <tr><td colSpan={9} className="px-3 py-10 text-center text-ink-muted">No sales in this date range.</td></tr>}
            {(data.items || []).map(s => (
              <tr key={s.id} data-testid={`rep-sales-row-${s.id}`}>
                <td className="px-2 py-1.5 font-mono text-xs">{s.invoice_no}</td>
                <td className="px-2 py-1.5 text-xs">{(s.created_at || "").slice(0, 10)}</td>
                <td className="px-2 py-1.5 text-xs capitalize">{s.sale_type}</td>
                <td className="px-2 py-1.5"><div className="text-xs font-medium">{s.patient_name || "—"}</div>{s.patient_uhid && <div className="font-mono text-[10px] text-ink-muted">{s.patient_uhid}</div>}</td>
                <td className="px-2 py-1.5 text-xs capitalize">{s.payment_mode}</td>
                <td className="px-2 py-1.5 text-right">₹ {Number(s.total).toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right text-emerald-700">₹ {Number(s.paid_amount || 0).toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right text-rose-700">₹ {Number(s.balance || 0).toFixed(2)}</td>
                <td className="px-2 py-1.5"><Badge variant="outline" className={s.status === "paid" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : s.status === "partial" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-rose-300 bg-rose-50 text-rose-700"}>{s.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function PurchasesReport() {
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [paymentMode, setPaymentMode] = useState("");
  const [status, setStatus] = useState("");
  const [data, setData] = useState({ items: [], summary: {} });

  const load = async () => {
    const params = new URLSearchParams({ from, to });
    if (paymentMode) params.set("payment_mode", paymentMode);
    if (status) params.set("status", status);
    const { data } = await api.get(`/pharmacy/reports/purchases?${params}`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, paymentMode, status]);

  const exportCsv = () => {
    const headers = [
      { label: "Invoice", key: "invoice_no" },
      { label: "Date", get: (r) => (r.created_at || "").slice(0, 10) },
      { label: "Supplier", key: "supplier" },
      { label: "GSTIN", key: "supplier_gstin" },
      { label: "Doc", key: "doc_type" },
      { label: "Mode", key: "payment_mode" },
      { label: "Status", key: "status" },
      { label: "Total", key: "total" },
      { label: "Paid", key: "paid_amount" },
      { label: "Balance", key: "balance" },
      { label: "CGST", key: "cgst_total" },
      { label: "SGST", key: "sgst_total" },
    ];
    downloadCsv(`pharmacy-purchases-${from}_${to}.csv`, toCsv(data.items || [], headers));
  };

  return (
    <div>
      <DateRangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} onExport={exportCsv}
        onPrint={() => openPdf("purchases", { from, to, payment_mode: paymentMode, status })}
        extra={<>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Mode</Label>
            <Select value={paymentMode || "all"} onValueChange={v => setPaymentMode(v === "all" ? "" : v)}>
              <SelectTrigger className="h-8 w-32"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>{["all","cash","upi","card","bank_transfer","credit"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-ink-muted">Status</Label>
            <Select value={status || "all"} onValueChange={v => setStatus(v === "all" ? "" : v)}>
              <SelectTrigger className="h-8 w-32"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent>{["all","paid","partial","due"].map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </>}
      />
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-5">
        <StatCard label="Bills" value={data.summary?.count ?? 0} />
        <StatCard label="Total ₹" value={`₹ ${Number(data.summary?.total || 0).toLocaleString("en-IN")}`} />
        <StatCard label="Paid ₹" value={`₹ ${Number(data.summary?.paid || 0).toLocaleString("en-IN")}`} tone="pos" />
        <StatCard label="Outstanding ₹" value={`₹ ${Number(data.summary?.outstanding || 0).toLocaleString("en-IN")}`} tone={Number(data.summary?.outstanding || 0) > 0 ? "neg" : "default"} />
        <StatCard label="GST ₹" value={`₹ ${Number(data.summary?.gst || 0).toLocaleString("en-IN")}`} />
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-wider text-ink-muted">
            <tr><th className="px-2 py-2 text-left">Date</th><th className="px-2 py-2 text-left">Supplier</th><th className="px-2 py-2 text-left">Doc</th><th className="px-2 py-2 text-left">Ref</th><th className="px-2 py-2 text-left">Mode</th><th className="px-2 py-2 text-right">Total</th><th className="px-2 py-2 text-right">Paid</th><th className="px-2 py-2 text-right">Balance</th><th className="px-2 py-2 text-left">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(data.items || []).length === 0 && <tr><td colSpan={9} className="px-3 py-10 text-center text-ink-muted">No purchases in this date range.</td></tr>}
            {(data.items || []).map(p => (
              <tr key={p.id} data-testid={`rep-purch-row-${p.id}`}>
                <td className="px-2 py-1.5 text-xs">{(p.created_at || "").slice(0, 10)}</td>
                <td className="px-2 py-1.5"><div className="text-xs font-medium">{p.supplier}</div>{p.supplier_gstin && <div className="font-mono text-[10px] text-ink-muted">{p.supplier_gstin}</div>}</td>
                <td className="px-2 py-1.5 text-xs"><Badge variant="outline" className={p.doc_type === "challan" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"}>{p.doc_type === "challan" ? "Challan" : "Invoice"}</Badge></td>
                <td className="px-2 py-1.5 font-mono text-xs">{p.invoice_no || "—"}</td>
                <td className="px-2 py-1.5 text-xs capitalize">{(p.payment_mode || "").replace("_", " ")}</td>
                <td className="px-2 py-1.5 text-right">₹ {Number(p.total).toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right text-emerald-700">₹ {Number(p.paid_amount || 0).toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right text-rose-700">₹ {Number(p.balance || 0).toFixed(2)}</td>
                <td className="px-2 py-1.5"><Badge variant="outline" className={p.status === "paid" ? "border-emerald-300 bg-emerald-50 text-emerald-700" : p.status === "partial" ? "border-amber-300 bg-amber-50 text-amber-700" : "border-rose-300 bg-rose-50 text-rose-700"}>{p.status || (p.payment_mode === "credit" ? "due" : "paid")}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function StockReport() {
  const [q, setQ] = useState("");
  const [lowOnly, setLowOnly] = useState(false);
  const [data, setData] = useState({ items: [], summary: {} });

  const load = async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (lowOnly) params.set("low_only", "true");
    const { data } = await api.get(`/pharmacy/reports/stock?${params}`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, lowOnly]);

  const exportCsv = () => {
    const headers = [
      { label: "Medicine", key: "name" }, { label: "Generic", key: "generic" }, { label: "Form", key: "form" },
      { label: "MRP", key: "mrp" }, { label: "Stock", key: "stock" }, { label: "GST %", key: "gst_percent" },
    ];
    downloadCsv("pharmacy-stock-" + today() + ".csv", toCsv(data.items || [], headers));
  };

  return (
    <div>
      <Card className="mb-3 p-3">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-[10px] uppercase tracking-wider text-ink-muted">Search</Label><Input value={q} onChange={e => setQ(e.target.value)} className="h-8 w-64" placeholder="Medicine name…" data-testid="rep-stock-q" /></div>
          <div className="flex items-center gap-2"><input id="rep-stock-low" type="checkbox" checked={lowOnly} onChange={e => setLowOnly(e.target.checked)} /><label htmlFor="rep-stock-low" className="text-sm">Low stock only (≤20)</label></div>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => openPdf("stock", { q, low_only: lowOnly ? "true" : "" })} data-testid="rep-stock-pdf"><Printer size={12} className="mr-1" />Print PDF</Button>
          <Button size="sm" variant="outline" onClick={exportCsv}><Download size={12} className="mr-1" />Export CSV</Button>
        </div>
      </Card>
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="SKUs" value={data.summary?.count ?? 0} />
        <StatCard label="Total units" value={Number(data.summary?.total_units || 0).toLocaleString("en-IN")} />
        <StatCard label="Stock value (MRP)" value={`₹ ${Number(data.summary?.stock_value_mrp || 0).toLocaleString("en-IN")}`} tone="pos" />
        <StatCard label="Low stock SKUs" value={data.summary?.low_stock_count ?? 0} tone={data.summary?.low_stock_count > 0 ? "warn" : "default"} />
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-wider text-ink-muted">
            <tr><th className="px-2 py-2 text-left">Medicine</th><th className="px-2 py-2 text-left">Generic</th><th className="px-2 py-2 text-left">Form</th><th className="px-2 py-2 text-right">MRP</th><th className="px-2 py-2 text-right">Stock</th><th className="px-2 py-2 text-right">GST %</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(data.items || []).length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-ink-muted">No medicines found.</td></tr>}
            {(data.items || []).map(m => (
              <tr key={m.id} data-testid={`rep-stock-row-${m.id}`}>
                <td className="px-2 py-1.5 font-medium">{m.name}</td>
                <td className="px-2 py-1.5 text-xs">{m.generic || "—"}</td>
                <td className="px-2 py-1.5 text-xs">{m.form || "—"}</td>
                <td className="px-2 py-1.5 text-right">₹ {Number(m.mrp || 0).toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right">{Number(m.stock || 0) <= 20 ? <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">{m.stock || 0}</Badge> : (m.stock || 0)}</td>
                <td className="px-2 py-1.5 text-right text-xs">{m.gst_percent || 0}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function SuppliersReport() {
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [data, setData] = useState({ items: [], summary: {} });

  const load = async () => {
    const { data } = await api.get(`/pharmacy/reports/suppliers?from=${from}&to=${to}`);
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);

  const exportCsv = () => {
    const headers = [
      { label: "Supplier", key: "name" }, { label: "GSTIN", key: "gstin" }, { label: "DL no.", key: "drug_license_no" },
      { label: "Terms", key: "payment_terms" }, { label: "Purchases (count)", key: "purchases" },
      { label: "Total", key: "total" }, { label: "Paid", key: "paid" }, { label: "Outstanding", key: "outstanding" },
      { label: "Opening bal.", key: "opening_balance" }, { label: "Net payable", key: "net_payable" },
    ];
    downloadCsv(`pharmacy-suppliers-${from}_${to}.csv`, toCsv(data.items || [], headers));
  };

  return (
    <div>
      <DateRangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} onExport={exportCsv}
        onPrint={() => openPdf("suppliers", { from, to })} />
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Suppliers" value={data.summary?.count ?? 0} />
        <StatCard label="Total purchases ₹" value={`₹ ${Number(data.summary?.total_purchases || 0).toLocaleString("en-IN")}`} />
        <StatCard label="Total paid ₹" value={`₹ ${Number(data.summary?.total_paid || 0).toLocaleString("en-IN")}`} tone="pos" />
        <StatCard label="Total payable ₹" value={`₹ ${Number(data.summary?.total_payable || 0).toLocaleString("en-IN")}`} tone={Number(data.summary?.total_payable || 0) > 0 ? "neg" : "default"} />
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-wider text-ink-muted">
            <tr><th className="px-2 py-2 text-left">Supplier</th><th className="px-2 py-2 text-left">GSTIN</th><th className="px-2 py-2 text-left">Terms</th><th className="px-2 py-2 text-right">Purchases</th><th className="px-2 py-2 text-right">Total</th><th className="px-2 py-2 text-right">Paid</th><th className="px-2 py-2 text-right">Outstanding</th><th className="px-2 py-2 text-right">Opening</th><th className="px-2 py-2 text-right">Net payable</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(data.items || []).length === 0 && <tr><td colSpan={9} className="px-3 py-10 text-center text-ink-muted">No suppliers.</td></tr>}
            {(data.items || []).map(s => (
              <tr key={s.id} data-testid={`rep-sup-row-${s.id}`}>
                <td className="px-2 py-1.5"><div className="font-medium">{s.name}</div>{s.contact_person && <div className="text-[10px] text-ink-muted">{s.contact_person}{s.phone ? ` · ${s.phone}` : ""}</div>}</td>
                <td className="px-2 py-1.5 font-mono text-xs">{s.gstin || "—"}</td>
                <td className="px-2 py-1.5 text-xs">{s.payment_terms || "—"}</td>
                <td className="px-2 py-1.5 text-right">{s.purchases}</td>
                <td className="px-2 py-1.5 text-right">₹ {Number(s.total).toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right text-emerald-700">₹ {Number(s.paid).toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right text-rose-700">₹ {Number(s.outstanding).toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right text-xs">₹ {Number(s.opening_balance).toFixed(2)}</td>
                <td className="px-2 py-1.5 text-right font-semibold" style={{ color: Number(s.net_payable) > 0 ? "#b91c1c" : "#0f766e" }}>₹ {Number(s.net_payable).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function NearExpiryReport() {
  const [days, setDays] = useState(60);
  const [data, setData] = useState({ items: [], summary: {} });
  const load = async () => { const { data } = await api.get(`/pharmacy/reports/near-expiry?days=${days}`); setData(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);
  const exportCsv = () => {
    const headers = [
      { label: "Medicine", key: "medicine_name" }, { label: "Batch", key: "batch_no" },
      { label: "Expiry", key: "expiry" }, { label: "Qty remaining", key: "qty_remaining" },
      { label: "Purchase rate", key: "purchase_rate" }, { label: "MRP", key: "mrp" },
    ];
    downloadCsv(`pharmacy-near-expiry-${today()}.csv`, toCsv(data.items || [], headers));
  };
  const todayStr = today();
  return (
    <div>
      <Card className="mb-3 p-3">
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-[10px] uppercase tracking-wider text-ink-muted">Days window</Label><Input type="number" value={days} onChange={e => setDays(parseInt(e.target.value) || 60)} className="h-8 w-24" data-testid="rep-exp-days" /></div>
          <div className="text-xs text-ink-muted">Showing batches expiring on or before <b>{data.cutoff || "—"}</b></div>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => openPdf("expiry", { days })} data-testid="rep-exp-pdf"><Printer size={12} className="mr-1" />Print PDF</Button>
          <Button size="sm" variant="outline" onClick={exportCsv}><Download size={12} className="mr-1" />Export CSV</Button>
        </div>
      </Card>
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-3">
        <StatCard label="Batches at risk" value={data.summary?.count ?? 0} tone="warn" />
        <StatCard label="Units at risk" value={Number(data.summary?.units_at_risk || 0).toLocaleString("en-IN")} />
        <StatCard label="Already expired" value={data.summary?.expired_count ?? 0} tone={data.summary?.expired_count > 0 ? "neg" : "default"} />
      </div>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-[10px] uppercase tracking-wider text-ink-muted">
            <tr><th className="px-2 py-2 text-left">Medicine</th><th className="px-2 py-2 text-left">Batch</th><th className="px-2 py-2 text-left">Expiry</th><th className="px-2 py-2 text-right">Qty</th><th className="px-2 py-2 text-right">MRP</th><th className="px-2 py-2 text-left">Status</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(data.items || []).length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-ink-muted">No batches expiring in this window.</td></tr>}
            {(data.items || []).map(b => {
              const expired = b.expiry && b.expiry < todayStr;
              return (
                <tr key={b.id} className={expired ? "bg-rose-50/50" : ""}>
                  <td className="px-2 py-1.5">{b.medicine_name}</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{b.batch_no}</td>
                  <td className="px-2 py-1.5 text-xs">{b.expiry}</td>
                  <td className="px-2 py-1.5 text-right">{b.qty_remaining}</td>
                  <td className="px-2 py-1.5 text-right text-xs">₹ {Number(b.mrp || 0).toFixed(2)}</td>
                  <td className="px-2 py-1.5"><Badge variant="outline" className={expired ? "border-rose-300 bg-rose-50 text-rose-700" : "border-amber-300 bg-amber-50 text-amber-700"}>{expired ? "Expired" : "Expiring soon"}</Badge></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function GstReport() {
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const [data, setData] = useState({ output_tax: {}, input_tax: {}, net_liability: {}, by_slab_sales: [] });
  const load = async () => { const { data } = await api.get(`/pharmacy/reports/gst?from=${from}&to=${to}`); setData(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to]);
  const exportCsv = () => {
    const out = data.output_tax || {}; const inp = data.input_tax || {}; const net = data.net_liability || {};
    const rows = [
      { Section: "Output GST (Sales)", Count: out.count, Taxable: out.taxable, CGST: out.cgst, SGST: out.sgst, "GST total": out.gst_total, "Grand total": out.grand_total },
      { Section: "Input GST (Purchases — invoice only)", Count: inp.count, Taxable: inp.taxable, CGST: inp.cgst, SGST: inp.sgst, "GST total": inp.gst_total, "Grand total": inp.grand_total },
      { Section: "Net liability (Output - Input)", Count: "", Taxable: "", CGST: net.cgst, SGST: net.sgst, "GST total": net.total, "Grand total": "" },
    ];
    const headers = ["Section", "Count", "Taxable", "CGST", "SGST", "GST total", "Grand total"].map(k => ({ label: k, key: k }));
    const slabHeaders = [{ label: "Slab", key: "slab" }, { label: "Taxable", key: "taxable" }, { label: "CGST", key: "cgst" }, { label: "SGST", key: "sgst" }];
    downloadCsv(`pharmacy-gst-${from}_${to}.csv`, toCsv(rows, headers) + "\n\nSlab-wise (Sales)\n" + toCsv(data.by_slab_sales || [], slabHeaders));
  };
  const Section = ({ title, d, tone = "default" }) => (
    <Card className="p-3">
      <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">{title}</div>
      <div className="space-y-1 text-sm">
        <div className="flex justify-between"><span className="text-ink-muted">Count</span><span>{d.count ?? 0}</span></div>
        <div className="flex justify-between"><span className="text-ink-muted">Taxable</span><span>₹ {Number(d.taxable || 0).toLocaleString("en-IN")}</span></div>
        <div className="flex justify-between"><span className="text-ink-muted">CGST</span><span>₹ {Number(d.cgst || 0).toLocaleString("en-IN")}</span></div>
        <div className="flex justify-between"><span className="text-ink-muted">SGST</span><span>₹ {Number(d.sgst || 0).toLocaleString("en-IN")}</span></div>
        <div className="flex justify-between border-t border-border pt-1 font-heading"><span>GST total</span><span className={tone === "pos" ? "text-emerald-700" : tone === "neg" ? "text-rose-700" : ""}>₹ {Number(d.gst_total || d.total || 0).toLocaleString("en-IN")}</span></div>
        {d.grand_total !== undefined && <div className="flex justify-between text-xs text-ink-muted"><span>Grand total</span><span>₹ {Number(d.grand_total).toLocaleString("en-IN")}</span></div>}
      </div>
    </Card>
  );
  return (
    <div>
      <DateRangeBar from={from} to={to} setFrom={setFrom} setTo={setTo} onExport={exportCsv}
        onPrint={() => openPdf("gst", { from, to })} />
      <div className="grid gap-3 md:grid-cols-3">
        <Section title="Output GST (Sales)" d={data.output_tax || {}} tone="pos" />
        <Section title="Input GST (Purchases · invoice)" d={data.input_tax || {}} />
        <Section title="Net liability (Output − Input)" d={data.net_liability || {}} tone={Number((data.net_liability || {}).total || 0) > 0 ? "neg" : "pos"} />
      </div>
      <Card className="mt-3 overflow-x-auto">
        <div className="border-b border-border bg-secondary px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-ink-muted">Slab-wise breakdown (Sales)</div>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-wider text-ink-muted">
            <tr><th className="px-3 py-2 text-left">Slab</th><th className="px-3 py-2 text-right">Taxable</th><th className="px-3 py-2 text-right">CGST</th><th className="px-3 py-2 text-right">SGST</th><th className="px-3 py-2 text-right">Total GST</th></tr>
          </thead>
          <tbody className="divide-y divide-border">
            {(data.by_slab_sales || []).length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-ink-muted">No sales in this date range.</td></tr>}
            {(data.by_slab_sales || []).map(s => (
              <tr key={s.slab}><td className="px-3 py-1.5 font-medium">{s.slab}</td><td className="px-3 py-1.5 text-right">₹ {Number(s.taxable).toFixed(2)}</td><td className="px-3 py-1.5 text-right">₹ {Number(s.cgst).toFixed(2)}</td><td className="px-3 py-1.5 text-right">₹ {Number(s.sgst).toFixed(2)}</td><td className="px-3 py-1.5 text-right font-semibold">₹ {(Number(s.cgst) + Number(s.sgst)).toFixed(2)}</td></tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
